import CleaningAlerts from "@/components/CleaningAlerts";
import { useEffect, useState, useMemo } from "react";
import { useNavigate } from "react-router-dom";
import { api } from "@/lib/api";
import { Badge } from "@/components/ui/badge";
import { Input } from "@/components/ui/input";
import { Select, SelectTrigger, SelectContent, SelectItem, SelectValue } from "@/components/ui/select";
import { SEVERITY, CATEGORIES, PROBLEM_STATUSES } from "@/lib/constants";
import { AlertTriangle, Search } from "lucide-react";

export default function Problems() {
  const [problems, setProblems] = useState([]);
  const [q, setQ] = useState("");
  const [sev, setSev] = useState("all");
  const [cat, setCat] = useState("all");
  const [st, setSt] = useState("all");
  const nav = useNavigate();

  useEffect(() => {
    api.get("/problems?include_resolved=false").then((r) => setProblems(r.data)).catch(() => {});
  }, []);

  const filtered = useMemo(() => {
    return problems.filter((p) => {
      if (sev !== "all" && p.severity !== sev) return false;
      if (cat !== "all" && p.category !== cat) return false;
      if (st !== "all" && p.status !== st) return false;
      if (q) {
        const hay = `${p.title} ${p.description} ${p.apartment?.name || ""} ${p.site?.name || ""}`.toLowerCase();
        if (!hay.includes(q.toLowerCase())) return false;
      }
      return true;
    });
  }, [problems, q, sev, cat, st]);

  return (
    <div className="p-4 md:p-8 max-w-7xl mx-auto">
      <div className="mb-6">
        <h1 className="text-2xl md:text-3xl font-bold font-display text-slate-900">Anomalies ouvertes</h1>
        <p className="text-sm text-slate-500 mt-1">{filtered.length} anomalie{filtered.length > 1 ? "s" : ""} active{filtered.length > 1 ? "s" : ""}</p>
      </div>

      <div className="mb-4"><CleaningAlerts /></div>

      <div className="grid md:grid-cols-4 gap-3 mb-6">
        <div className="bg-white rounded-xl border border-slate-200 p-2 flex items-center gap-2">
          <Search className="w-4 h-4 text-slate-400 ml-1" />
          <Input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Rechercher…"
                 data-testid="problems-search"
                 className="border-0 focus-visible:ring-0 shadow-none" />
        </div>
        <Select value={sev} onValueChange={setSev}>
          <SelectTrigger data-testid="problems-filter-sev"><SelectValue placeholder="Gravité" /></SelectTrigger>
          <SelectContent>
            <SelectItem value="all">Toutes gravités</SelectItem>
            <SelectItem value="mineur">Mineure</SelectItem>
            <SelectItem value="majeur">Majeure</SelectItem>
            <SelectItem value="critique">Critique</SelectItem>
          </SelectContent>
        </Select>
        <Select value={cat} onValueChange={setCat}>
          <SelectTrigger data-testid="problems-filter-cat"><SelectValue placeholder="Catégorie" /></SelectTrigger>
          <SelectContent>
            <SelectItem value="all">Toutes catégories</SelectItem>
            {CATEGORIES.map((c) => <SelectItem key={c} value={c}>{c}</SelectItem>)}
          </SelectContent>
        </Select>
        <Select value={st} onValueChange={setSt}>
          <SelectTrigger data-testid="problems-filter-status"><SelectValue placeholder="Statut" /></SelectTrigger>
          <SelectContent>
            <SelectItem value="all">Tous statuts</SelectItem>
            {PROBLEM_STATUSES.filter((s) => s !== "Résolu").map((s) => <SelectItem key={s} value={s}>{s}</SelectItem>)}
          </SelectContent>
        </Select>
      </div>

      <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-3">
        {filtered.map((p) => {
          const s = SEVERITY[p.severity];
          return (
            <div key={p.id} onClick={() => p.apartment_id && nav(`/apartments/${p.apartment_id}?tab=problems`)}
                 data-testid={`problem-item-${p.id}`}
                 className={`bg-white rounded-2xl border border-slate-200 p-4 shadow-sm ${p.apartment_id ? "hover:border-slate-300 cursor-pointer" : ""}`}>
              <div className="flex items-start justify-between gap-2 mb-2">
                <div className="font-semibold text-slate-900">{p.room || p.title}</div>
                <Badge className={`${s.cls} border flex-shrink-0`}>{s.label}</Badge>
              </div>
              <div className="text-xs text-slate-500 mb-2 flex items-center gap-1">
                <AlertTriangle className="w-3 h-3" />{p.apartment?.name || p.site?.name || "—"}
                {p.site && (
                  <span className="px-1.5 py-0.5 rounded text-[10px] font-bold text-white flex-shrink-0"
                        style={{ background: p.site.type === "hq" ? "#B8860B" : "#0E7490" }}>
                    {p.site.type === "hq" ? "QG" : "Bureau ménage"}
                  </span>
                )}
              </div>
              <div className="flex items-center justify-between text-xs text-slate-400 mt-2">
                <span>{p.category}</span>
                <span>{p.status}</span>
              </div>
              <div className="text-xs text-slate-400 mt-1">{new Date(p.created_at).toLocaleString("fr-FR")}</div>
            </div>
          );
        })}
        {filtered.length === 0 && (
          <div className="col-span-full bg-white rounded-xl border border-slate-200 p-10 text-center text-slate-400">
            Aucun problème
          </div>
        )}
      </div>
    </div>
  );
}
